import type { SolvedShape } from '../analytics';
import type { GeometryElement } from '../geometrypad.model';
import type { PointPort, EdgePort } from './port.model';
import type { PortRegistry } from './port-registry';

// ---------------------------------------------------------------------------
// Opposite-edge lookups (shared with feature creators)
// ---------------------------------------------------------------------------

const TRIANGLE_OPPOSITE_EDGE: Record<string, string> = {
  A: 'edge.BC',
  B: 'edge.CA',
  C: 'edge.AB',
};

const QUAD_OPPOSITE_EDGE: Record<string, string> = {
  A: 'edge.top',
  B: 'edge.top',
  C: 'edge.bottom',
  D: 'edge.bottom',
};

// ---------------------------------------------------------------------------
// Feature port registration helpers
// ---------------------------------------------------------------------------

function registerDiagonalPorts(
  element: GeometryElement,
  ports: Record<string, () => unknown>,
  registry: PortRegistry,
): void {
  const params = element.params as { of: string; vertices?: [string, string] };
  const parentId = params.of;

  const getA = registry.getGetter(`${parentId}.point.A`);
  const getB = registry.getGetter(`${parentId}.point.B`);
  const getC = registry.getGetter(`${parentId}.point.C`);
  const getD = registry.getGetter(`${parentId}.point.D`);

  const drawBoth = !params.vertices;
  const drawD1 =
    drawBoth ||
    (params.vertices![0] === 'A' && params.vertices![1] === 'C') ||
    (params.vertices![0] === 'C' && params.vertices![1] === 'A');
  const drawD2 =
    drawBoth ||
    (params.vertices![0] === 'B' && params.vertices![1] === 'D') ||
    (params.vertices![0] === 'D' && params.vertices![1] === 'B');

  if (drawD1) {
    ports['d1'] = () => dist(getA() as PointPort, getC() as PointPort);
  }
  if (drawD2) {
    ports['d2'] = () => dist(getB() as PointPort, getD() as PointPort);
  }
  if (drawD1 && drawD2) {
    ports['intersection'] = () =>
      lineLineIntersection(getA() as PointPort, getC() as PointPort, getB() as PointPort, getD() as PointPort);
  }
}

function registerAltitudePorts(
  element: GeometryElement,
  ports: Record<string, () => unknown>,
  registry: PortRegistry,
): void {
  const params = element.params as { of: string; from: string };
  const parentId = params.of;
  const fromVertex = params.from;

  const getVertex = registry.getGetter(`${parentId}.point.${fromVertex}`);
  const oppositeEdgeName = TRIANGLE_OPPOSITE_EDGE[fromVertex] ?? QUAD_OPPOSITE_EDGE[fromVertex];
  if (!oppositeEdgeName) {
    return;
  }
  const getEdge = registry.getGetter(`${parentId}.${oppositeEdgeName}`);

  ports['length'] = () => {
    const v = getVertex() as PointPort;
    const e = getEdge() as EdgePort;
    const f = perpendicularFoot(v, e.start, e.end);
    return dist(v, f);
  };
  ports['foot'] = () => {
    const v = getVertex() as PointPort;
    const e = getEdge() as EdgePort;
    return perpendicularFoot(v, e.start, e.end);
  };
}

function registerMedianPorts(
  element: GeometryElement,
  ports: Record<string, () => unknown>,
  registry: PortRegistry,
): void {
  const params = element.params as { of: string; from: string };
  const parentId = params.of;
  const fromVertex = params.from;

  const getVertex = registry.getGetter(`${parentId}.point.${fromVertex}`);
  const oppositeEdgeName = TRIANGLE_OPPOSITE_EDGE[fromVertex];
  if (!oppositeEdgeName) {
    return;
  }
  const getEdge = registry.getGetter(`${parentId}.${oppositeEdgeName}`);

  ports['length'] = () => {
    const v = getVertex() as PointPort;
    const e = getEdge() as EdgePort;
    const m = mid(e.start, e.end);
    return dist(v, m);
  };
  ports['midpoint'] = () => {
    const e = getEdge() as EdgePort;
    return mid(e.start, e.end);
  };
}

function registerMidsegmentPorts(
  element: GeometryElement,
  ports: Record<string, () => unknown>,
  registry: PortRegistry,
): void {
  const params = element.params as { of: string; edges: [string, string] };
  const parentId = params.of;
  const [rawEdge1, rawEdge2] = params.edges;

  const edgeName1 = resolveEdgeName(rawEdge1, parentId, registry);
  const edgeName2 = resolveEdgeName(rawEdge2, parentId, registry);

  const getEdge1 = registry.getGetter(`${parentId}.edge.${edgeName1}`);
  const getEdge2 = registry.getGetter(`${parentId}.edge.${edgeName2}`);

  ports['length'] = () => {
    const e1 = getEdge1() as EdgePort;
    const e2 = getEdge2() as EdgePort;
    return dist(mid(e1.start, e1.end), mid(e2.start, e2.end));
  };
  ports['midpoint1'] = () => {
    const e1 = getEdge1() as EdgePort;
    return mid(e1.start, e1.end);
  };
  ports['midpoint2'] = () => {
    const e2 = getEdge2() as EdgePort;
    return mid(e2.start, e2.end);
  };
}

function resolveEdgeName(name: string, parentId: string, registry: PortRegistry): string {
  if (registry.hasPort(`${parentId}.edge.${name}`)) {
    return name;
  }
  const reversed = name.split('').reverse().join('');
  if (registry.hasPort(`${parentId}.edge.${reversed}`)) {
    return reversed;
  }
  return name;
}

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

function dist(a: PointPort, b: PointPort): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}

function mid(a: PointPort, b: PointPort): PointPort {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function perpendicularFoot(p: PointPort, start: PointPort, end: PointPort): PointPort {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) {
    return [start[0], start[1]];
  }
  const t = ((p[0] - start[0]) * dx + (p[1] - start[1]) * dy) / lenSq;
  return [start[0] + t * dx, start[1] + t * dy];
}

function lineLineIntersection(a: PointPort, b: PointPort, c: PointPort, d: PointPort): PointPort {
  const bax = b[0] - a[0];
  const bay = b[1] - a[1];
  const dcx = d[0] - c[0];
  const dcy = d[1] - c[1];
  const cax = c[0] - a[0];
  const cay = c[1] - a[1];
  const denom = bax * dcy - bay * dcx;
  if (Math.abs(denom) < 1e-12) {
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  }
  const t = (cax * dcy - cay * dcx) / denom;
  return [a[0] + t * bax, a[1] + t * bay];
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

const FEATURE_TYPES = new Set(['Diagonal', 'Altitude', 'Median', 'Midsegment']);

/**
 * Register preliminary ports from solved shapes so subsequent elements can reference them.
 * These are static ports based on solved values (not yet on JSXGraph objects).
 */
export function registerSolvedPortsOnRegistry(
  element: GeometryElement,
  solved: SolvedShape | null,
  vertices: Record<string, [number, number]>,
  registry: PortRegistry,
): void {
  if (!solved) {
    const ports: Record<string, () => unknown> = {};
    for (const [name, coord] of Object.entries(vertices)) {
      ports[name] = () => coord;
    }

    // Polygon edge and edge-length ports
    if (element.type === 'Polygon') {
      const vertexKeys = Object.keys(vertices)
        .filter((k) => k.startsWith('point['))
        .sort((a, b) => {
          const ai = parseInt(a.match(/\d+/)![0], 10);
          const bi = parseInt(b.match(/\d+/)![0], 10);
          return ai - bi;
        });
      const n = vertexKeys.length;
      for (let i = 0; i < n; i++) {
        const start = vertices[vertexKeys[i]];
        const end = vertices[vertexKeys[(i + 1) % n]];
        ports[`edge[${i}]`] = () => ({ start, end });
        ports[`edge[${i}].length`] = () => {
          const dx = end[0] - start[0];
          const dy = end[1] - start[1];
          return Math.sqrt(dx * dx + dy * dy);
        };
      }
    }

    // Feature ports — computed lazily from parent shape ports
    if (FEATURE_TYPES.has(element.type)) {
      if (element.type === 'Diagonal') {
        registerDiagonalPorts(element, ports, registry);
      } else if (element.type === 'Altitude') {
        registerAltitudePorts(element, ports, registry);
      } else if (element.type === 'Median') {
        registerMedianPorts(element, ports, registry);
      } else if (element.type === 'Midsegment') {
        registerMidsegmentPorts(element, ports, registry);
      }
    }

    registry.register(element.id, ports);
    return;
  }

  // Register all solved values as static ports.
  // Coordinate-dependent ports (point.*, edge.*) use the `vertices` parameter
  // so they can be re-registered with world-space coords after placement.
  const ports: Record<string, () => unknown> = {};

  for (const [name, coord] of Object.entries(vertices)) {
    ports[name] = () => coord;
  }

  if ('sides' in solved) {
    // Triangle — scalar ports from solved data
    const tri = solved as {
      sides: Record<string, number>;
      angles: Record<string, number>;
      exteriorAngles: Record<string, number>;
      area: number;
      perimeter: number;
    };
    for (const [k, v] of Object.entries(tri.sides)) {
      ports[k] = () => v;
    }
    for (const [k, v] of Object.entries(tri.angles)) {
      ports[k] = () => v;
    }
    for (const [k, v] of Object.entries(tri.exteriorAngles)) {
      ports[`ext${k}`] = () => v;
    }
    ports['area'] = () => tri.area;
    ports['perimeter'] = () => tri.perimeter;

    // Vertex points — from `vertices` parameter (may be world-space)
    for (const k of Object.keys(vertices)) {
      ports[`point.${k}`] = () => vertices[k];
    }

    // Edges — from `vertices` parameter
    ports['edge.AB'] = () => ({ start: vertices['A'], end: vertices['B'] });
    ports['edge.BC'] = () => ({ start: vertices['B'], end: vertices['C'] });
    ports['edge.CA'] = () => ({ start: vertices['C'], end: vertices['A'] });
  } else if ('w' in solved || ('s' in solved && !('d1' in solved))) {
    // Rectangle or Square
    const quad = solved as { d: number; area: number; perimeter: number };

    if ('w' in solved) {
      const rect = solved as { w: number; h: number };
      ports['w'] = () => rect.w;
      ports['h'] = () => rect.h;
    }
    if ('s' in solved) {
      ports['s'] = () => (solved as { s: number }).s;
    }

    ports['d'] = () => quad.d;
    ports['area'] = () => quad.area;
    ports['perimeter'] = () => quad.perimeter;

    // Vertex points — from `vertices` parameter
    for (const k of Object.keys(vertices)) {
      ports[`point.${k}`] = () => vertices[k];
    }

    // Edges — from `vertices` parameter
    ports['edge.bottom'] = () => ({ start: vertices['A'], end: vertices['B'] });
    ports['edge.right'] = () => ({ start: vertices['B'], end: vertices['C'] });
    ports['edge.top'] = () => ({ start: vertices['C'], end: vertices['D'] });
    ports['edge.left'] = () => ({ start: vertices['D'], end: vertices['A'] });
  } else if ('d1' in solved && 'd2' in solved) {
    // Parallelogram, Rhombus, or Trapezoid
    const quad = solved as { area: number; perimeter: number; h: number; d1: number; d2: number };

    ports['h'] = () => quad.h;
    ports['d1'] = () => quad.d1;
    ports['d2'] = () => quad.d2;
    ports['area'] = () => quad.area;
    ports['perimeter'] = () => quad.perimeter;

    if ('b' in solved && 's' in solved && !('a' in solved)) {
      const pg = solved as { b: number; s: number; angle: number };
      ports['b'] = () => pg.b;
      ports['s'] = () => pg.s;
      ports['angle'] = () => pg.angle;
    } else if ('s' in solved && !('b' in solved)) {
      const rh = solved as { s: number; angle: number };
      ports['s'] = () => rh.s;
      ports['angle'] = () => rh.angle;
    } else if ('a' in solved && 'b' in solved) {
      const tz = solved as { a: number; b: number; angle: number; s1: number; s2: number };
      ports['a'] = () => tz.a;
      ports['b'] = () => tz.b;
      ports['angle'] = () => tz.angle;
      ports['s1'] = () => tz.s1;
      ports['s2'] = () => tz.s2;
    }

    // Vertex points — from `vertices` parameter
    for (const k of Object.keys(vertices)) {
      ports[`point.${k}`] = () => vertices[k];
    }

    // Edges — from `vertices` parameter
    ports['edge.bottom'] = () => ({ start: vertices['A'], end: vertices['B'] });
    ports['edge.right'] = () => ({ start: vertices['B'], end: vertices['C'] });
    ports['edge.top'] = () => ({ start: vertices['C'], end: vertices['D'] });
    ports['edge.left'] = () => ({ start: vertices['D'], end: vertices['A'] });
  } else if ('radius' in solved) {
    // Circle — center from `vertices` parameter, scalars from solved
    const circ = solved as { radius: number; area: number; circumference: number };
    ports['center'] = () => vertices['center'];
    ports['radius'] = () => circ.radius;
    ports['area'] = () => circ.area;
    ports['circumference'] = () => circ.circumference;
  }

  registry.register(element.id, ports);
}
